// src/utils/advancedStatsCalculator.js

/**
 * Palette de couleurs pour les graphiques
 */
const COLORS = {
  service: '#D55E00',      // Rouge brique
  coupDroit: '#0072B2',    // Bleu
  revers: '#009E73',       // Vert bleuté
  volee: '#E69F00',        // Orange
  erreursAdv: '#95a5a6',   // Gris
  retour: '#CC79A7',       // Violet
  pointsAdv: '#95a5a6'     // Gris
};

/**
 * Mapping des couleurs par type de coup
 */
const SHOT_COLORS = {
  'Service': '#D55E00',
  'Retour': '#CC79A7',
  'Coup Droit': '#0072B2',
  'Revers': '#009E73',
  'Volée': '#E69F00'
};

/**
 * 🆕 Filtre les points libres (familleCode = "?") avant tout calcul de stats avancées
 */
const filterPointsLibres = (points) => {
  const filtered = points.filter(p => (p.pointData?.familleCode || '') !== '?');
  const excluded = points.length - filtered.length;
  if (excluded > 0) {
    console.log(`📊 advancedStats: ${excluded} point(s) libre(s) "?" exclus des calculs`);
  }
  return filtered;
};

/**
 * Calcule toutes les statistiques avancées pour les 2 joueurs
 * @param {Array} points - Tableau des points du match
 * @param {Array} filteredPoints - Points filtrés selon le set (optionnel, si null = tous les points)
 * @returns {Object} Toutes les stats avancées
 */
const calculateAllAdvancedStats = (points, filteredPoints = null) => {
  // 🆕 Filtrer les points libres dans les deux tableaux
  const pts = filterPointsLibres(filteredPoints || points);
  
  return {
    // Stats par joueur
    j1: calculatePlayerAdvancedStats(pts, 'J1', 'J2'),
    j2: calculatePlayerAdvancedStats(pts, 'J2', 'J1'),
    
    // Stats comparatives
    comparison: calculateComparisonStats(pts),
    shotsByWinner: calculateShotsByWinner(pts),
    zoneStats: calculateZoneStats(pts),
    directionStats: calculateDirectionStats(pts),
    rallyLengthStats: calculateRallyLengthStats(pts)
  };
};

/**
 * Calcule les stats avancées pour un joueur
 */
 const calculatePlayerAdvancedStats = (points, playerCode, opponentCode) => {
  return {
    pointsWon: calculatePointsWon(points, playerCode),
    pointsLost: calculatePointsLost(points, playerCode, opponentCode)
  };
};

/**
 * 1. RÉPARTITION DES POINTS GAGNÉS
 */
 const calculatePointsWon = (points, playerCode) => {
  let service = 0;
  let retour = 0;
  let coupDroit = 0;
  let revers = 0;
  let volee = 0;
  let erreursAdv = 0;

  points.forEach((point) => {
    if (point.winner !== playerCode) return;

    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const resultatCode = data.resultatCode || '';

    // Si code contient F = Faute de l'adversaire
    if (resultatCode.includes('F')) {
      erreursAdv++;
    } 
    // Sinon = Point gagnant du joueur, répartir par famille
    else {
      if (familleCode === 'S') service++;
      else if (familleCode === 'T') retour++;
      else if (familleCode === 'C') coupDroit++;
      else if (familleCode === 'R') revers++;
      else if (familleCode === 'V') volee++;
      // "?" déjà filtré en amont, pas besoin de le gérer ici
    }
  });

  return [
    { name: 'Service', value: service, color: COLORS.service },
    { name: 'Retour', value: retour, color: COLORS.retour },
    { name: 'Coup Droit', value: coupDroit, color: COLORS.coupDroit },
    { name: 'Revers', value: revers, color: COLORS.revers },
    { name: 'Volée', value: volee, color: COLORS.volee },
    { name: 'Erreurs Adversaire', value: erreursAdv, color: COLORS.erreursAdv }
  ].filter(item => item.value > 0);
};

/**
 * 2. RÉPARTITION DES POINTS PERDUS
 */
 const calculatePointsLost = (points, playerCode, opponentCode) => {
  let service = 0;
  let retour = 0;
  let coupDroit = 0;
  let revers = 0;
  let volee = 0;
  let pointsAdv = 0;

  points.forEach(point => {
    if (point.winner !== opponentCode) return;

    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const resultatCode = data.resultatCode || '';

    // Si code contient F = Faute du joueur, répartir par famille
    if (resultatCode.includes('F')) {
      if (familleCode === 'S') service++;
      else if (familleCode === 'T') retour++;
      else if (familleCode === 'C') coupDroit++;
      else if (familleCode === 'R') revers++;
      else if (familleCode === 'V') volee++;
    } 
    // Sinon = Point gagnant de l'adversaire
    else {
      pointsAdv++;
    }
  });

  return [
    { name: 'Fautes Service', value: service, color: COLORS.service },
    { name: 'Fautes Retour', value: retour, color: COLORS.retour },
    { name: 'Fautes Coup Droit', value: coupDroit, color: COLORS.coupDroit },
    { name: 'Fautes Revers', value: revers, color: COLORS.revers },
    { name: 'Fautes Volée', value: volee, color: COLORS.volee },
    { name: 'Points Adversaire', value: pointsAdv, color: COLORS.pointsAdv }
  ].filter(item => item.value > 0);
};

/**
 * 3. COMPARAISON DES COUPS ENTRE J1 ET J2
 */
 const calculateComparisonStats = (points) => {
  let j1 = {
    pointsService: 0, pointsRetour: 0, pointsCoupDroit: 0, pointsRevers: 0, pointsVolee: 0,
    fautesService: 0, fautesRetour: 0, fautesCoupDroit: 0, fautesRevers: 0, fautesVolee: 0
  };

  let j2 = { ...j1 };

  points.forEach(point => {
    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const resultatCode = data.resultatCode || '';
    const winner = point.winner;

    // Points gagnants (pas de F dans resultatCode)
    if (!resultatCode.includes('F')) {
      if (winner === 'J1') {
        if (familleCode === 'S') j1.pointsService++;
        else if (familleCode === 'T') j1.pointsRetour++;
        else if (familleCode === 'C') j1.pointsCoupDroit++;
        else if (familleCode === 'R') j1.pointsRevers++;
        else if (familleCode === 'V') j1.pointsVolee++;
      } else if (winner === 'J2') {
        if (familleCode === 'S') j2.pointsService++;
        else if (familleCode === 'T') j2.pointsRetour++;
        else if (familleCode === 'C') j2.pointsCoupDroit++;
        else if (familleCode === 'R') j2.pointsRevers++;
        else if (familleCode === 'V') j2.pointsVolee++;
      }
    }
    // Fautes (avec F dans resultatCode)
    else {
      // La faute est comptabilisée pour le perdant
      if (winner === 'J1') {
        // J2 a perdu (fait la faute)
        if (familleCode === 'S') j2.fautesService++;
        else if (familleCode === 'T') j2.fautesRetour++;
        else if (familleCode === 'C') j2.fautesCoupDroit++;
        else if (familleCode === 'R') j2.fautesRevers++;
        else if (familleCode === 'V') j2.fautesVolee++;
      } else if (winner === 'J2') {
        // J1 a perdu (fait la faute)
        if (familleCode === 'S') j1.fautesService++;
        else if (familleCode === 'T') j1.fautesRetour++;
        else if (familleCode === 'C') j1.fautesCoupDroit++;
        else if (familleCode === 'R') j1.fautesRevers++;
        else if (familleCode === 'V') j1.fautesVolee++;
      }
    }
  });

  return { j1, j2 };
};

/**
 * 4. COUPS EXPLIQUANT LA VICTOIRE/DÉFAITE (tous points confondus)
 */
 const calculateShotsByWinner = (points) => {
  const winnersCount = {};
  const losersCount = {};
  
  points.forEach(point => {
    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const resultatCode = data.resultatCode || '';
    
    // Déterminer le nom du coup
    let shotName = 'Autre';
    if (familleCode === 'S') shotName = 'Service';
    else if (familleCode === 'T') shotName = 'Retour';
    else if (familleCode === 'C') shotName = 'Coup Droit';
    else if (familleCode === 'R') shotName = 'Revers';
    else if (familleCode === 'V') shotName = 'Volée';
    
    if (resultatCode.includes('F')) {
      // Faute = coup du perdant
      losersCount[shotName] = (losersCount[shotName] || 0) + 1;
    } else {
      // Point gagnant
      winnersCount[shotName] = (winnersCount[shotName] || 0) + 1;
    }
  });
  
  // Convertir en tableau
  const toArray = (obj, colorMap) => Object.entries(obj)
    .map(([name, value]) => ({
      name,
      value,
      color: colorMap[name] || '#666'
    }))
    .sort((a, b) => b.value - a.value);
  
  return {
    winners: toArray(winnersCount, SHOT_COLORS),
    losers: toArray(losersCount, SHOT_COLORS)
  };
};

/**
 * 5. STATISTIQUES PAR ZONE DE SERVICE
 */
 const calculateZoneStats = (points) => {
  const zones = ['1', '2', '3', '4', '5', '6'];
  const zoneLabels = [
    'Ext. Égalité', 'Corps Égalité', 'T Égalité',
    'T Avantage', 'Corps Avantage', 'Ext. Avantage'
  ];

  const serviceStats = {
    j1: { aces: Array(6).fill(0), doubleFautes: Array(6).fill(0) },
    j2: { aces: Array(6).fill(0), doubleFautes: Array(6).fill(0) }
  };

  const retourStats = {
    j1: { points: Array(6).fill(0), fautes: Array(6).fill(0) },
    j2: { points: Array(6).fill(0), fautes: Array(6).fill(0) }
  };

  points.forEach(point => {
    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const resultatCode = data.resultatCode || '';
    const winner = point.winner;

    // Pour S et T, la zone est dans sousTypeCode (ex: S4 → sousType='4')
    // zoneCode est disponible pour les saisies avancées, sinon on utilise sousTypeCode
    const zoneCode = data.zoneCode || (
      (familleCode === 'S' || familleCode === 'T') ? data.sousTypeCode : null
    );
    
    if (!zoneCode || !zones.includes(String(zoneCode))) return;
    const zoneIndex = parseInt(zoneCode) - 1;

    // Services
    if (familleCode === 'S') {
      if (resultatCode.includes('F')) {
        // Doubles fautes
        if (winner === 'J2') serviceStats.j1.doubleFautes[zoneIndex]++;
        else if (winner === 'J1') serviceStats.j2.doubleFautes[zoneIndex]++;
      } else {
        // Aces / Services gagnants
        if (winner === 'J1') serviceStats.j1.aces[zoneIndex]++;
        else if (winner === 'J2') serviceStats.j2.aces[zoneIndex]++;
      }
    }
    
    // Retours
    if (familleCode === 'T') {
      if (resultatCode.includes('F')) {
        // Fautes en retour
        if (winner === 'J2') retourStats.j1.fautes[zoneIndex]++;
        else if (winner === 'J1') retourStats.j2.fautes[zoneIndex]++;
      } else {
        // Points gagnants en retour
        if (winner === 'J1') retourStats.j1.points[zoneIndex]++;
        else if (winner === 'J2') retourStats.j2.points[zoneIndex]++;
      }
    }
  });

  return { serviceStats, retourStats, zoneLabels };
};

/**
 * 6. STATISTIQUES PAR DIRECTION (Coup Droit / Revers / Volée)
 */
 const calculateDirectionStats = (points) => {
  const coupDroitDirections = [
    { code: 'C', label: 'Croisé' },
    { code: 'E', label: 'Au centre' },
    { code: 'L', label: 'Long de ligne' },
    { code: 'P', label: 'Passing/Lob' },
    { code: 'O', label: 'Inside out' },
    { code: 'M', label: 'Inside middle' },
    { code: 'I', label: 'Inside in' },
    { code: 'A', label: 'Amorti' }
  ];

  const reversDirections = [
    { code: 'C', label: 'Croisé' },
    { code: 'E', label: 'Au centre' },
    { code: 'L', label: 'Long de ligne' },
    { code: 'P', label: 'Passing/Lob' },
    { code: 'A', label: 'Amorti' },
    { code: 'S', label: 'Slice' }
  ];

  const volleeDirections = [
    { code: 'C', label: 'Coup droit' },
    { code: 'R', label: 'Revers' },
    { code: 'A', label: 'Amortie' },
    { code: 'S', label: 'Smash' }
  ];

  // Initialiser les stats
  const coupDroitStats = {
    j1: { points: {}, fautes: {} },
    j2: { points: {}, fautes: {} }
  };
  const reversStats = {
    j1: { points: {}, fautes: {} },
    j2: { points: {}, fautes: {} }
  };
  const volleeStats = {
    j1: { points: {}, fautes: {} },
    j2: { points: {}, fautes: {} }
  };

  // Initialiser tous les compteurs à 0
  coupDroitDirections.forEach(dir => {
    coupDroitStats.j1.points[dir.code] = 0;
    coupDroitStats.j1.fautes[dir.code] = 0;
    coupDroitStats.j2.points[dir.code] = 0;
    coupDroitStats.j2.fautes[dir.code] = 0;
  });
  reversDirections.forEach(dir => {
    reversStats.j1.points[dir.code] = 0;
    reversStats.j1.fautes[dir.code] = 0;
    reversStats.j2.points[dir.code] = 0;
    reversStats.j2.fautes[dir.code] = 0;
  });
  volleeDirections.forEach(dir => {
    volleeStats.j1.points[dir.code] = 0;
    volleeStats.j1.fautes[dir.code] = 0;
    volleeStats.j2.points[dir.code] = 0;
    volleeStats.j2.fautes[dir.code] = 0;
  });

  points.forEach(point => {
    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const sousTypeCode = data.sousTypeCode;
    const resultatCode = data.resultatCode || '';
    const winner = point.winner;

    if (!sousTypeCode) return;

    // Coup droit
    if (familleCode === 'C') {
      if (resultatCode.includes('F')) {
        if (winner === 'J2') coupDroitStats.j1.fautes[sousTypeCode] = (coupDroitStats.j1.fautes[sousTypeCode] || 0) + 1;
        else if (winner === 'J1') coupDroitStats.j2.fautes[sousTypeCode] = (coupDroitStats.j2.fautes[sousTypeCode] || 0) + 1;
      } else {
        if (winner === 'J1') coupDroitStats.j1.points[sousTypeCode] = (coupDroitStats.j1.points[sousTypeCode] || 0) + 1;
        else if (winner === 'J2') coupDroitStats.j2.points[sousTypeCode] = (coupDroitStats.j2.points[sousTypeCode] || 0) + 1;
      }
    }

    // Revers
    if (familleCode === 'R') {
      if (resultatCode.includes('F')) {
        if (winner === 'J2') reversStats.j1.fautes[sousTypeCode] = (reversStats.j1.fautes[sousTypeCode] || 0) + 1;
        else if (winner === 'J1') reversStats.j2.fautes[sousTypeCode] = (reversStats.j2.fautes[sousTypeCode] || 0) + 1;
      } else {
        if (winner === 'J1') reversStats.j1.points[sousTypeCode] = (reversStats.j1.points[sousTypeCode] || 0) + 1;
        else if (winner === 'J2') reversStats.j2.points[sousTypeCode] = (reversStats.j2.points[sousTypeCode] || 0) + 1;
      }
    }

    // Volée
    if (familleCode === 'V') {
      if (resultatCode.includes('F')) {
        if (winner === 'J2') volleeStats.j1.fautes[sousTypeCode] = (volleeStats.j1.fautes[sousTypeCode] || 0) + 1;
        else if (winner === 'J1') volleeStats.j2.fautes[sousTypeCode] = (volleeStats.j2.fautes[sousTypeCode] || 0) + 1;
      } else {
        if (winner === 'J1') volleeStats.j1.points[sousTypeCode] = (volleeStats.j1.points[sousTypeCode] || 0) + 1;
        else if (winner === 'J2') volleeStats.j2.points[sousTypeCode] = (volleeStats.j2.points[sousTypeCode] || 0) + 1;
      }
    }
  });

  return {
    coupDroitStats,
    reversStats,
    volleeStats,
    coupDroitDirections,
    reversDirections,
    volleeDirections
  };
};

/**
 * 7. STATISTIQUES PAR LONGUEUR DE RALLYE
 */
 const calculateRallyLengthStats = (points) => {
  const categories = [
    { code: '1-2', label: '1-2 frappes' },
    { code: '3', label: '3-4 frappes' },
    { code: '5', label: '5-8 frappes' },
    { code: '9', label: '9+ frappes' }
  ];

  const stats = {
    j1: { pointsGagnes: {}, pointsPerdus: {} },
    j2: { pointsGagnes: {}, pointsPerdus: {} }
  };

  // Initialiser tous les compteurs
  categories.forEach(cat => {
    stats.j1.pointsGagnes[cat.code] = 0;
    stats.j1.pointsPerdus[cat.code] = 0;
    stats.j2.pointsGagnes[cat.code] = 0;
    stats.j2.pointsPerdus[cat.code] = 0;
  });

  points.forEach(point => {
    const data = point.pointData || {};
    const familleCode = data.familleCode;
    const nbFrappesCode = data.nbFrappesCode;
    const winner = point.winner;

    let category = null;

    // Service (S) et Retour (T) → 1-2 frappes
    if (familleCode === 'S' || familleCode === 'T') {
      category = '1-2';
    } else if (nbFrappesCode) {
      if (nbFrappesCode === 3) category = '3';
      else if (nbFrappesCode === 5) category = '5';
      else if (nbFrappesCode === 9) category = '9';
    }

    if (!category) return;

    // Comptabiliser les points gagnés et perdus
    if (winner === 'J1') {
      stats.j1.pointsGagnes[category]++;
      stats.j2.pointsPerdus[category]++;
    } else if (winner === 'J2') {
      stats.j2.pointsGagnes[category]++;
      stats.j1.pointsPerdus[category]++;
    }
  });

  return { stats, categories };
};

export {
  calculateAllAdvancedStats,
  calculatePlayerAdvancedStats,
  calculatePointsWon,
  calculatePointsLost,
  calculateComparisonStats,
  calculateShotsByWinner,
  calculateZoneStats,
  calculateDirectionStats,
  calculateRallyLengthStats,
  COLORS,
  SHOT_COLORS
};